import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { environment } from '../../environments/environment';

/** Endpoints que não recebem Authorization (login, refresh, OAuth). */
const PUBLIC_API_PATTERNS = ['/authenticate', '/auth/google', '/users/register', '/portal/config', '/refresh_authenticate'];
const ACTIVE_INSTITUTION_KEY = 'liessin_last_institution_id';

function isPublicApiRequest(url: string, baseUrl: string): boolean {
  if (!url.startsWith(baseUrl)) return false;
  const path = new URL(url, window.location.origin).pathname;
  return PUBLIC_API_PATTERNS.some((p) => path.endsWith(p));
}

export function resolveInstitutionIdForRequest(url: string, storage: Storage = localStorage): string | null {
  const institutionMatch = url.match(/\/institutions\/(\d+)/);
  if (institutionMatch) {
    storage.setItem(ACTIVE_INSTITUTION_KEY, institutionMatch[1]);
    return institutionMatch[1];
  }
  return storage.getItem(ACTIVE_INSTITUTION_KEY);
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const token = auth.getAccessToken();
  const baseUrl = environment.baseUrl;
  const isAuthRequest = isPublicApiRequest(req.url, baseUrl);

  if (token && req.url.startsWith(baseUrl) && !isAuthRequest) {
    const headers: { [header: string]: string } = {
      Authorization: `Bearer ${token}`
    };

    const lastInstitutionId = resolveInstitutionIdForRequest(req.url);
    if (lastInstitutionId) {
      headers['X-Institution-Id'] = lastInstitutionId;
    }

    req = req.clone({
      setHeaders: headers,
    });
  }

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401 && token && !req.url.includes('refresh_authenticate')) {
        return from(auth.refreshSession()).pipe(
          switchMap((newToken) => {
            if (newToken) {
              const retry = req.clone({
                setHeaders: { Authorization: `Bearer ${newToken}` },
              });
              return next(retry);
            }
            return throwError(() => err);
          })
        );
      }
      return throwError(() => err);
    })
  );
};
